import { IVideoWidgetData, IVideoWidgetStyle, VideoWidgetData } from "@pageBuilder/typings";
import { CssProperties } from "src/models/css-property";
import { IRenderingCommonContext } from "@commonRendering/types";
import { WidgetRendererBase } from "../widget-renderer-base";
import { VideoScriptRenderer } from "./video-script.renderer";

export class VideoWidgetRenderer extends WidgetRendererBase<IVideoWidgetData> {

    private readonly scriptRenderer: VideoScriptRenderer;

    public constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);

        this.scriptRenderer = new VideoScriptRenderer();
    }

    protected createNewInternal(): HTMLElement {
        const element = this.createWidgetElement("videoWidget");
        element.classList.add("video", "aspect-ratio-resizable");

        return element;
    }

    protected renderInternal(element: HTMLElement, data: IVideoWidgetData): HTMLElement {
        data.controls ??= VideoWidgetData.defaultData.controls;

        this.renderVideo(element, data);
        this.renderStyles(element, data);

        return element;
    }

    protected renderStyles(element: HTMLElement, data: IVideoWidgetData): void {
        this.renderBaseStyles(element, data.style);

        if (!data.style?.videoStyle) {
            return;
        }

        this.renderVideoStyles(element, data);
        this.renderScripts(element);
    }

    private renderScripts(element: HTMLElement): void {
        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);
    }

    private renderBaseStyles(element: HTMLElement, style: IVideoWidgetStyle | null): void {
        element.classList.add("aspect-ratio-resizable");

        element.style.paddingTop = style?.heightData?.paddingTop
            ? `${style.heightData.paddingTop}%`
            : `${VideoWidgetData.defaultStyle.heightData?.paddingTop}%`; // 16:9

        this.animationRenderer.setAnimationStyles(element, style?.animation);
    }

    private renderVideoStyles(element: HTMLElement, data: IVideoWidgetData): void {
        const videoWrapperProps = new CssProperties();
        const videoProps = new CssProperties();

        const isCoverMode = data.style.videoStyle?.fitMode === "cover";
        if (isCoverMode) {
            videoWrapperProps.tryAdd("width", "100%");
            videoWrapperProps.tryAdd("height", "100%");
        } else {
            videoWrapperProps.tryAdd("aspect-ratio", `${data.aspectRatio}`);
        }

        this.styleService.setStyle(element, `#${element.id} .widget-content .video-wrapper`, videoWrapperProps.data);
        this.styleService.setStyle(element, `#${element.id} .widget-content .video`, videoProps.data);
    }

    private renderVideo(element: HTMLElement, data: IVideoWidgetData): void {
        let content = element.querySelector<HTMLElement>(".widget-content");
        if (!content) {
            content = this.createContentElement();
            element.appendChild(content);
        }

        if (this.isEditorRendering) {
            this.renderForEditorMode(element, content, data);
            return;
        }

        if (!data.src || data.videoType === "embed" && !data.embedType) { // nothing to render
            return;
        }

        const videoWrapper = document.createElement("div");
        videoWrapper.classList.add("video-wrapper");

        if (data.videoType === "upload") {
            videoWrapper.appendChild(this.generateUploadVideoHtml(data));
        } else {
            videoWrapper.appendChild(this.generateEmbedVideoHtml(data));
        }

        content.appendChild(videoWrapper);
    }

    private renderForEditorMode(element: HTMLElement, content: HTMLElement, data: IVideoWidgetData): void {
        const video = element.querySelector("video");
        const videoPlaceholderElement = element.querySelector(".video-placeholder");
        const notAvailableElement = content.querySelector(".not-available");
        video?.remove();
        videoPlaceholderElement?.remove();
        notAvailableElement?.remove();

        if (data.videoType === "upload") {
            this.setupUploadVideo(content, element, data);
        } else {
            this.setupEmbedVideo(content, element, data);
        }

        if (data.src) {
            element.dataset.videoSource = data.src;
        }
    }

    private setupUploadVideo(content: HTMLElement, element: HTMLElement, data: IVideoWidgetData): void {
        if (data.src) {
            content.appendChild(this.createPlaceholderElement(element, data));
        } else {
            content.appendChild(this.createNotAvailableElement());
        }
    }

    private setupEmbedVideo(content: HTMLElement, element: HTMLElement, data: IVideoWidgetData): void {
        if (data.embedType) {
            content.appendChild(this.createPlaceholderElement(element, data));
        } else {
            content.appendChild(this.createNotAvailableElement());
        }
    }

    private generateUploadVideoHtml(data: IVideoWidgetData): HTMLVideoElement {
        const videoElement = document.createElement("video");
        videoElement.classList.add("video");

        if (data.controls) {
            videoElement.setAttribute("controls", "controls");
        }

        if (data.autoplay) {
            videoElement.setAttribute("autoplay", "autoplay");
            videoElement.setAttribute("muted", "muted");
        }

        if (data.loop) {
            videoElement.setAttribute("loop", "loop");
        }

        videoElement.setAttribute("disablepictureinpicture", "");
        videoElement.setAttribute("controlslist", "nofullscreen nodownload");

        if (data.placeholder?.thumbnailUrl) {
            videoElement.setAttribute("poster", data.placeholder.thumbnailUrl);
        }

        if (data.placeholder?.position) {
            videoElement.style.objectPosition = `${data.placeholder.position.offsetX}% ${data.placeholder.position.offsetY}%`;
        }

        const sourceElement = document.createElement("source");
        sourceElement.src = data.src;
        videoElement.appendChild(sourceElement);

        return videoElement;
    }

    private generateEmbedVideoHtml(data: IVideoWidgetData): HTMLIFrameElement {
        if (data.embedType === "youtube") {
            return this.generateYoutubeVideoHtml(data);
        }

        return this.generateVimeoVideoHtml(data);
    }

    private generateYoutubeVideoHtml(data: IVideoWidgetData): HTMLIFrameElement {
        const iframeElement = document.createElement("iframe");
        iframeElement.classList.add("video");

        iframeElement.style.width = "100%";
        iframeElement.style.height = "100%";
        iframeElement.width = "100%";
        iframeElement.height = "100%";

        const youtubeOptions = `autoplay=${data.autoplay ? "1" : "0"}&controls=${data.controls ? "1" : "0"}&loop=${data.loop ? "1&playlist=" + data.embedId : "0"}&mute=${data.autoplay ? "1" : "0"}`;
        iframeElement.src = `https://www.youtube.com/embed/${data.embedId}?${youtubeOptions}`;

        iframeElement.title = "External video link";
        iframeElement.setAttribute("frameborder", "0");
        iframeElement.setAttribute("allow", "accelerometer; encrypted-media; gyroscope; picture-in-picture; autoplay");
        iframeElement.setAttribute("allowfullscreen", "true");

        return iframeElement;
    }

    private generateVimeoVideoHtml(data: IVideoWidgetData): HTMLIFrameElement {
        const iframeElement = document.createElement("iframe");
        iframeElement.classList.add("video");

        iframeElement.style.width = "100%";
        iframeElement.style.height = "100%";
        iframeElement.width = "100%";
        iframeElement.height = "100%";

        const vimeoOptions = `autoplay=${data.autoplay ? "1" : "0"}&controls=${data.controls ? "1" : "0"}&loop=${data.loop ? "1" : "0"}&muted=${data.autoplay ? "1" : "0"}`;
        iframeElement.src = `https://player.vimeo.com/video/${data.embedId}?${vimeoOptions}`;

        iframeElement.title = "External video link";
        iframeElement.setAttribute("frameborder", "0");
        iframeElement.setAttribute("allow", "accelerometer; encrypted-media; gyroscope; picture-in-picture; autoplay");
        iframeElement.setAttribute("allowfullscreen", "true");

        return iframeElement;
    }

    private createPlaceholderElement(element: HTMLElement, data: IVideoWidgetData): HTMLElement {
        const placeholderElement = document.createElement("div");
        placeholderElement.classList.add("video-placeholder");

        if (!data.placeholder?.thumbnailUrl) {
            return placeholderElement;
        }

        const imageElement = document.createElement("img");
        imageElement.classList.add("thumbnail");
        imageElement.src = data.placeholder.thumbnailUrl;
        imageElement.setAttribute("draggable", "false");

        const imageWrapper = document.createElement("div");
        imageWrapper.classList.add("preview");
        imageWrapper.appendChild(imageElement);

        placeholderElement.appendChild(imageWrapper);

        const placeholderProps = new CssProperties();
        if (data.style.videoStyle?.fitMode === "cover") {
            placeholderProps.tryAdd("width", "100%");
            placeholderProps.tryAdd("height", "100%");
        } else {
            placeholderProps.tryAdd("aspect-ratio", `${data.aspectRatio}`);
        }

        if (data.placeholder.position) {
            const thumbnailProps = new CssProperties();
            thumbnailProps.tryAdd("object-position", `${data.placeholder.position.offsetX}% ${data.placeholder.position.offsetY}%`);
            this.styleService.setStyle(element, `#${element.id} .thumbnail`, thumbnailProps.data);
        }

        this.styleService.setStyle(element, `#${element.id} .video-placeholder`, placeholderProps.data);

        return placeholderElement;
    }

    private createNotAvailableElement(): HTMLElement {
        const notAvailableElement = document.createElement("div");
        notAvailableElement.classList.add("not-available");
        return notAvailableElement;
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [
            `#${element.id}`,
            `#${element.id} .video`,
            `#${element.id} .thumbnail`,
            `#${element.id} .video-placeholder`,
            `#${element.id} .video-wrapper`,
        ];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

}
